import { api } from '@/services/api';
import type {
  IDadosExcelResponse,
  IHistoricoVersaoItem,
  INaoAbatePayload,
  IRelatorioTransacoesAgrupadoResponse,
  IRelatorioTransacoesParams,
  TTransactionTipoParam,
} from '@/types/relatorioTransacoes';
import type { IHistoricoPublicacao } from '@/types/publicacao';

export const relatorioTransacoesKeys = {
  all: ['relatorio-transacoes-agrupadas'] as const,
  agrupado: (params: IRelatorioTransacoesParams) => ['relatorio-transacoes-agrupadas', params] as const,
};

export function fetchRelatorioTransacoesAgrupado(
  params: IRelatorioTransacoesParams,
): Promise<IRelatorioTransacoesAgrupadoResponse> {
  const query = new URLSearchParams({ ano: params.ano, tipo: params.tipo });
  if (params.mes) query.set('mes', params.mes);
  return api.get<IRelatorioTransacoesAgrupadoResponse>(`/relatorios/transacoes-agrupadas?${query}`);
}

export function publicarProducaoMes(mes: string, unidadeId: string): Promise<unknown> {
  const query = new URLSearchParams({ tipo: 'producao', mes, unidade_id: unidadeId });
  return api.post(`/publicacao?${query}`);
}

export function publicarVendas(dataInicio: string, dataFim: string, unidadeId: string): Promise<{ message?: string }> {
  return api.post(`/publicacao/vendas?data_inicio=${dataInicio}&data_fim=${dataFim}&unidade_id=${unidadeId}`);
}

export function publicarCompras(dataInicio: string, dataFim: string, unidadeId: string): Promise<{ message?: string }> {
  return api.post(`/publicacao/compras?data_inicio=${dataInicio}&data_fim=${dataFim}&unidade_id=${unidadeId}`);
}

export function fetchHistoricoPublicacoes(): Promise<{ items: IHistoricoPublicacao[] }> {
  return api.get('/publicacao/historico?page=1&page_size=100');
}

/** Detalhes de lote/diagnóstico/parte afetada de um item do relatório, pro modal 'Resumo produtos'. */
export function fetchResumoProdutos(tipo: TTransactionTipoParam, id: string): Promise<Record<string, unknown>[]> {
  const query = new URLSearchParams({ tipo, id });
  return api.get<Record<string, unknown>[]>(`/relatorios/transacoes-agrupadas/resumo-produtos?${query}`);
}

interface IDadosExcelParams {
  tipo: TTransactionTipoParam;
  id?: string;
  mes?: string;
  unidadeId?: string;
}

/** Dados sintéticos + analíticos pro front montar o arquivo Excel (abate ou expedição/vendas). */
export function fetchDadosExcel({ tipo, id, mes, unidadeId }: IDadosExcelParams): Promise<IDadosExcelResponse> {
  const query = new URLSearchParams({ tipo });
  if (id) query.set('id', id);
  if (mes) query.set('mes', mes);
  if (unidadeId) query.set('unidade_id', unidadeId);
  return api.get<IDadosExcelResponse>(`/relatorios/transacoes-agrupadas/dados-excel?${query}`);
}

/**
 * Todas as versões (ativa + inativas) de um item, pro modal 'Histórico'. Informe `id`
 * (registro de referência) ou `data` (caso 'dia sem abate', sem registro ativo pra
 * identificar a unidade — busca em todas as unidades acessíveis ao usuário).
 */
export function fetchHistoricoVersoes(
  tipo: TTransactionTipoParam,
  params: { id: string } | { data: string },
): Promise<IHistoricoVersaoItem[]> {
  const query = new URLSearchParams({ tipo, ...params });
  return api.get<IHistoricoVersaoItem[]>(`/relatorios/transacoes-agrupadas/historico?${query}`);
}

export function publicarAbate(id: string): Promise<unknown> {
  const query = new URLSearchParams({ tipo: 'abate', id });
  return api.post(`/publicacao?${query}`);
}

/** Registra 'não abate' (dia sem abate) — cria um registro local (sem publicar no PGA). */
export function publicarNaoAbate(payload: INaoAbatePayload): Promise<unknown> {
  return api.post('/abate', payload);
}
